// What agents reached for in YOUR connected inbox, and what the confidentiality
// guard held back - the owner's view, and the only place a thread is released.
// One card per inbox you authorised; nobody else's appears here.
//
// See docs/Y2026-Q3/design-mailbox-confidentiality-guard.md sections 6.4, 6.6.
import { useState } from "react";

import {
  useMailboxReadLog,
  useReleaseThread,
  type MailboxReadLogInbox,
  type MailboxReadLogThread,
} from "../api";
import { ViewAiTraceButton } from "../components/ViewAiTraceButton";
import { formatRelative } from "../utils/formatRelative";
import styles from "./IntegrationsRoute.module.css";
import own from "./MailboxReadLogSection.module.css";

const TOOL_LABELS: Record<string, string> = {
  search_mailbox: "Searched",
  read_thread: "Read a thread",
  ingest_threads: "Asked to ingest",
};

const describe = (row: MailboxReadLogThread): string => {
  if (row.last_outcome === "served") {
    return row.confidentiality_class === null ? "Allowed - not yet checked" : "Allowed";
  }
  // A key to an account, not a privacy call; nothing releases it.
  if (row.confidentiality_class === "credential" || row.current_class === "credential") {
    return "Withheld - sign-in or security mail";
  }
  // Work mail read with its credentials taken out; withheld only when that could not
  // be done afresh.
  if (row.confidentiality_class === "contains_secret" || row.current_class === "contains_secret") {
    return "Withheld - carries credentials";
  }
  // A cautious call, not a judgement that the mail is private - say which.
  if (row.confidentiality_class === "unsure") return "Withheld - classifier unsure";
  if (row.confidentiality_class !== null) return "Withheld as personal";
  // Unjudged at the time. Say where it stands now: most such threads are judged
  // shortly after, and a row that only says "could not be checked" reads as a
  // standing problem when it usually is not one.
  if (row.current_class === "business") return "Withheld then, not yet checked - since cleared";
  if (row.current_class === "personal") return "Withheld then, not yet checked - since judged personal";
  return "Withheld - not yet checked";
};

// Nothing to release once the thread is readable anyway.
const releasable = (row: MailboxReadLogThread): boolean =>
  row.withheld_reaches > 0 &&
  !!row.project_id &&
  row.current_class !== "business" &&
  row.current_class !== "credential" &&
  row.confidentiality_class !== "credential" &&
  // A release would show it with its credentials in.
  row.current_class !== "contains_secret" &&
  row.confidentiality_class !== "contains_secret";

const ReleaseButton = ({ row }: { row: MailboxReadLogThread }) => {
  const release = useReleaseThread();
  const [confirming, setConfirming] = useState(false);
  if (!releasable(row)) return null;
  if (row.released_here) {
    const stillHeld = release.data?.documents_still_held ?? 0;
    return (
      <>
        <span className={own.released}>Released</span>
        {stillHeld > 0 ? (
          <p className={own.error}>
            Still held back on this project: the conversation includes another thread that
            is withheld. Release that one too to open it.
          </p>
        ) : null}
      </>
    );
  }

  if (!confirming) {
    return (
      <button
        type="button"
        className={own.releaseBtn}
        onClick={() => setConfirming(true)}
      >
        Release to this project
      </button>
    );
  }
  return (
    <div className={own.confirm}>
      {/* The consequences, stated rather than implied. A release reads like a
          temporary unlock unless it says otherwise, and it is not one. */}
      <p className={own.confirmText}>
        This adds the thread to <strong>{row.project_id}</strong> permanently. Every
        InRange staff member will then be able to read it through the document
        tools, and undoing this later will not remove it from the record of any
        agent run that read it meanwhile. If you only need one part of it,
        forwarding that part by hand is the smaller step.
      </p>
      <div className={own.confirmRow}>
        <button
          type="button"
          className={own.releaseBtn}
          disabled={release.isLoading}
          onClick={() =>
            release.mutate({
              mailbox_id: row.mailbox_id,
              gmail_thread_id: row.gmail_thread_id,
              project_id: row.project_id as string,
            })
          }
        >
          {release.isLoading ? "Releasing…" : "Yes, release it"}
        </button>
        <button
          type="button"
          className={own.cancelBtn}
          onClick={() => setConfirming(false)}
        >
          Cancel
        </button>
      </div>
      {release.isError ? (
        <p className={own.error}>
          Could not release it.{" "}
          {release.error instanceof Error ? release.error.message : ""}
        </p>
      ) : null}
    </div>
  );
};

// Windows the owner can reason about. "Everything" last, because the useful
// question is almost always "what happened recently", not "what ever happened".
const WINDOWS: { label: string; days: number | null }[] = [
  { label: "Last 24 hours", days: 1 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Everything", days: null },
];

const InboxReadLog = ({ inbox }: { inbox: MailboxReadLogInbox }) => {
  // Collapsed by default: a reassurance surface, not a working one. It matters
  // that it exists and that the count is visible; the rows are for the occasion
  // when somebody wants to check or release something.
  const [open, setOpen] = useState(false);
  const [showServed, setShowServed] = useState(false);
  const [days, setDays] = useState<number | null>(7);
  const log = useMailboxReadLog({
    mailboxId: inbox.mailbox_id,
    outcome: showServed ? "all" : "withheld",
    sinceDays: days,
  });
  // The page-level fetch already answered the DEFAULT view, so use it until the
  // card's own read lands - but only for that view, or a changed filter would
  // briefly show the old window's counts as if they were the new one's.
  const isDefaultView = !showServed && days === 7;
  const data = log.data?.mailboxes[0] ?? (isDefaultView ? inbox : null);
  if (data === null) {
    return (
      <section className={styles.section}>
        <h2 className={styles.h2}>Held back from agents for your inbox - {inbox.account_email}</h2>
        {log.isError ? (
          <p className={own.error}>
            Could not load this view. {log.error.message}{" "}
            <button type="button" className={own.toggle} onClick={() => void log.refetch()}>
              Retry
            </button>
          </p>
        ) : (
          <p className={styles.lede}>Loading...</p>
        )}
      </section>
    );
  }
  const withheld = data.counts.withheld;
  const windowLabel = (WINDOWS.find((w) => w.days === days)?.label ?? "Everything").toLowerCase();

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <h2 className={styles.h2}>
          Held back from agents for your inbox - {inbox.account_email}{" "}
          <span className={styles.count}>{withheld}</span>
        </h2>
        <button
          type="button"
          className={own.toggle}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>
      <p className={styles.lede}>
        {withheld > 0
          ? `Agents were held back from ${withheld} thread${withheld === 1 ? "" : "s"} in your inbox in the ${windowLabel}. If one genuinely bears on a project, you can release it to that project.`
          : `Agents were not held back from anything in your inbox in the ${windowLabel}.`}{" "}
        Only you see this.
      </p>
      {open ? (
        <>
          <div className={own.controls}>
            <label className={own.control}>
              <span className={own.controlLabel}>Period</span>
              <select
                className={own.select}
                value={days === null ? "all" : String(days)}
                onChange={(e) => setDays(e.target.value === "all" ? null : Number(e.target.value))}
              >
                {WINDOWS.map((w) => (
                  <option key={w.label} value={w.days === null ? "all" : String(w.days)}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={own.control}>
              <input
                type="checkbox"
                checked={showServed}
                onChange={(e) => setShowServed(e.target.checked)}
              />
              <span className={own.controlLabel}>
                Also show what agents were allowed to read ({data.counts.served})
              </span>
            </label>
          </div>
          {data.threads.length === 0 ? (
            <div className={styles.empty}>
              {showServed
                ? "No agent reached into your inbox in this period."
                : "Nothing was held back in this period."}
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Last reached</th>
                    <th>What</th>
                    <th>Thread</th>
                    <th>Outcome</th>
                    <th>Asked by</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.threads.map((row) => (
                    <tr key={`${row.gmail_thread_id}:${row.project_id ?? ""}`}>
                      <td className={styles.nowrap}>
                        {formatRelative(row.last_at)}
                        {row.reaches > 1 ? (
                          <div className={own.participants}>reached {row.reaches} times</div>
                        ) : null}
                      </td>
                      <td>
                        {row.tools.map((t) => TOOL_LABELS[t] ?? t).join(", ")}
                        {row.queries.map((q) => (
                          // The queries, so the owner can re-run them in Gmail and
                          // see exactly what the agent would have seen.
                          <div key={q} className={own.query}>
                            <code>{q}</code>
                          </div>
                        ))}
                      </td>
                      <td>
                        {row.subject ? (
                          <>
                            <div>{row.subject}</div>
                            {row.participants.length > 0 ? (
                              <div className={own.participants}>
                                {row.participants.slice(0, 3).join(", ")}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <code className={own.threadId}>{row.gmail_thread_id}</code>
                        )}
                      </td>
                      <td className={styles.nowrap}>
                        <span
                          className={row.last_outcome === "withheld" ? own.withheld : own.served}
                        >
                          {describe(row)}
                        </span>
                      </td>
                      <td>{row.requested_by.length ? row.requested_by.join(", ") : "-"}</td>
                      <td>
                        <ReleaseButton row={row} />
                        {/* Owner-gated server-side; "no trace" when a cached
                            verdict was used rather than a call made. */}
                        <ViewAiTraceButton
                          surface="mailbox_confidentiality"
                          artefactId={row.run_id}
                          label="Why?"
                          hasTrace={row.run_id !== null}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.capped ? (
            <p className={styles.lede}>
              Showing the most recent {data.threads.length} threads; there are older entries.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export const MailboxReadLogSection = () => {
  const log = useMailboxReadLog();
  if (log.isError) {
    // Said rather than hidden: an owner who sees nothing would read it as
    // "nothing was held back".
    return (
      <section className={styles.section}>
        <h2 className={styles.h2}>Held back from agents for your inbox</h2>
        <p className={own.error}>
          Could not load what agents reached for in your inbox. {log.error.message}{" "}
          <button type="button" className={own.toggle} onClick={() => void log.refetch()}>
            Retry
          </button>
        </p>
      </section>
    );
  }
  // Somebody with no connected inbox has nothing to see here, and saying so
  // would be noise on a page that is mostly about something else.
  if (!log.data || log.data.mailboxes.length === 0) return null;
  return (
    <>
      {log.data.mailboxes.map((inbox) => (
        <InboxReadLog key={inbox.mailbox_id} inbox={inbox} />
      ))}
    </>
  );
};
